import { AxiosError } from 'axios/index';
import store from '../../store';
import { payments } from '../../services/';
import { ErrorLoggerEvent } from '../../services/payment-sdk/models/error-logger-event';
import { PaymentProvider } from '../../services/adaptors/meshPayments';
import { updateDialog, updateNotification } from '../../modules/notification';
import { Cart } from '../../modules/cart/types';
import { Customer, Address } from '../../modules/customer/types';
import { Store } from '../../modules/delivery/types';
import { getSelectedPaymentMethod, selectPaymentMethod, updateStockCheckData } from "../../modules/billing";
import newRelicData from '../../newrelic';

const billing = store.getState().billing;
const selectedPayment = getSelectedPaymentMethod(billing);

/**
 * This function will try to cast an error we typically get from a 'catch(error)'
 * into the error type of the payment service.
 * @param error Can be any type.
 * @return An object with error data if possible, null otherwise.
**/
export function getPaymentServiceErrorResponseData(error: unknown) {
    const errorData: any = (error as AxiosError<ErrorLoggerEvent>).response?.data;
    if (errorData) {
        return {
            internalCode: errorData.InternalCode,
            errorCode: errorData.ErrorCode,
            errorType: errorData.ErrorType,
            errorLevel: errorData.ErrorLevel,
            errorMessage: errorData.ErrorMessage,
        }
    }
    return null;
}

/**
 * The Payment service returns adyen errors as stringified JSON inside of a longer string.
 * This function attempts to isolate the stringified JSON part of this string, parse it, and return
 * the result as a proper object.
 * @param errorString
 * @returns Object, or undefined if the no adyen error pattern could be found
 */
function parsePaymentServiceAdyenError(errorString: string): AdyenError | undefined {
    try {
        const matches = errorString.match(/\{[\w":, ]+\}/);
        if (matches && matches.length > 0) {
            return JSON.parse(matches[0]);
        }
    }
    catch (error) {
        return;
    }
}

type AdyenError = {
    status?: number,
    errorCode?: string,
    message?: string,
    errorType?: string,
    pspReference?: string,
};

/**
 * Handle errors from payment service calls depending on the error 'internalCode'
 * @param error
 */
export function catchPaymentServiceError(error: unknown) {
    const errorResponse = getPaymentServiceErrorResponseData(error);
    store.dispatch(updateStockCheckData({paymentID: ''}));
    if (errorResponse) {
        newRelicData({ actionName: 'catchPaymentServiceError', userFunction: 'catchPaymentServiceError', message: { errorCode: errorResponse.internalCode, msg: errorResponse.errorMessage }, paymentName: selectedPayment.name, result: 'failure'});
        // Handle initPayment known errors
        switch (errorResponse.internalCode) {
            case "CART_LOCK_06": // Out of stock or Fluent error
            case "STOCK_3": // Out of stock error
                store.dispatch(updateDialog('out_of_stock_return_to_cart'));
                break;

            case "EXTERNAL_PAYMENT_ERROR_1": { // something went wrong with payment gateway
                const searchParams = new URLSearchParams(window.location.search);
                const errorCodeCheck = searchParams.has('error_code');
                if(errorCodeCheck) {
                    const errorCode = searchParams.get('error_code');
                    newRelicData({ actionName: 'networkError', userFunction: 'catchPaymentServiceError', message: { errorCode: errorCode, msg: 'EXTERNAL_PAYMENT_ERROR_1' }, paymentName: selectedPayment.name, result: 'failure'});
                    let errorCodeValue = 'payment_gateway_error';
                    switch (errorCode) {
                        case 'CC_05' :
                        case 'CC_54' :
                        case 'CC_14' :
                            errorCodeValue = errorCode;
                    }
                    window.location.href = `/delivery?notification=${errorCodeValue}`;
                } else {
                    if (window.location.pathname.includes('/paymentredirect')) {
                        window.location.href = `/delivery?notification=payment_gateway_error`;
                    }
                    else {
                        store.dispatch(updateNotification('payment_gateway_error'));
                    }
                }
                break;
            }

            case "EXTERNAL_PAYMENT_ERROR_2": { // Mismatch between final cart amount and initpayment amount
                if (window.location.pathname.includes('/paymentredirect')) {
                    window.location.href = `/delivery?notification=transaction_capture_failed_incorrect_amount`;
                }
                else {
                    store.dispatch(updateNotification('transaction_capture_failed_incorrect_amount'));
                }
                break;
            }

            case "CART_LOCK_01": // Unable to parse request
            case "CART_LOCK_02": // Unable to create mesh request from provided data
            case "CART_LOCK_03": // Unable to make request to Platform
            case "CART_LOCK_04": // Bad request to lock cart
            case "CART_LOCK_05": // Cart not found
            case "CART_LOCK_07": // Error parsing cart lock response
            case "CART_LOCK_08": // Cart lock failed in Platform
            case "ORDER_CREATE_01": // Bad request, unable to parse request
            case "ORDER_CREATE_02": // Error calling platform
            case "ORDER_CREATE_03": // Order creation failed
            case "ORDER_CREATE_04": // Unable to parse Platform response
            case "GATEWAY_ERROR_01": // Gateway not enabled for store
            default: // Other error from the initPayment error system
                const adyenError = parsePaymentServiceAdyenError(errorResponse.errorMessage);
                if (window.location.pathname.includes('/paymentredirect')) {
                    store.dispatch(updateDialog('adyen_issue_on_redirection', {content: adyenError?.message, title: adyenError?.errorCode}));
                } else if (adyenError?.errorCode || adyenError?.message) {
                    store.dispatch(updateNotification('adyen_error', {
                        code: adyenError.errorCode,
                        message: adyenError.message,
                    }));
                }
                else {
                    //error message for shopback payment method
                    if (errorResponse.errorMessage && errorResponse.errorMessage.includes('We are unable to send the verification code to your phone.')) { 
                        store.dispatch(updateNotification('shopback_invalid_phone_number'));
                    }
                    else {
                        const paymentMethod = getSelectedPaymentMethod(store.getState().billing);
                        if (paymentMethod.id === 'CARD') {
                            store.dispatch(selectPaymentMethod(''));
                            store.dispatch(selectPaymentMethod(paymentMethod.id));
                        }
                        store.dispatch(updateNotification('payment_service_error', {
                            code: errorResponse.errorCode,
                            message: errorResponse.errorMessage,
                        }));
                    }
                }
                break;
        }
    }
    // Any other error we can't predict
    else {
        store.dispatch(updateNotification('other_error'));
    }
    return null;
}

/**
 * Wrapper function for the payment service function initPayment(). Adds error handling depending on the 'InternalCode' error returned.
 * @param multiple All params required by initPayment()
 * @returns The initPayment() response, or undefined if an error was encountered
 */
export async function initPaymentCatchErrors(
    isExpress: boolean,
    provider: PaymentProvider,
    method: string,
    merchantReference: string,
    cart: Cart,
    customer: Customer,
    billingAddress: Address,
    deliveryAddress: Address,
    selectedStore: Store|null,
    locale: string,
    paymentID: string | undefined,
    initPayload: string) {
    try {
        const initResult = await payments().initPayment(
            isExpress,
            provider,
            method,
            merchantReference,
            cart,
            customer,
            billingAddress,
            deliveryAddress,
            selectedStore,
            locale,
            paymentID,
            initPayload
        );
        return initResult;
    }
    catch (error) {
        newRelicData({ actionName: 'networkError', userFunction: 'initPaymentCatchErrors', message: {  paymentProvider:  provider, error: (error as Error).message },  paymentName: selectedPayment.name, result: 'failure' });
        catchPaymentServiceError(error);
    }
}

/**
 * Wrapper function for the payment service function finalisePayment(). Adds error handling depending on the 'InternalCode' error returned.
 * @param multiple All params required by finalisePayment()
 * @returns The finalisePayment() response, or undefined if an error was encountered
 */
export async function finalisePaymentCatchErrors(
    provider: PaymentProvider,
    method: string,
    gatewayReference: string,
    initResultCode: string,
    transactionID: string,
) {
    try {
        const finaliseResult = await payments().finalisePayment(
            provider,
            method,
            gatewayReference,
            initResultCode,
            transactionID,
        );
        return finaliseResult;
    }
    catch (error) {
        newRelicData({ actionName: 'networkError', userFunction: 'finalisePaymentCatchErrors', message: { paymentProvider:  provider, error: (error as Error).message },  paymentName: selectedPayment.name, result: 'failure'})
        catchPaymentServiceError(error);
    }
}
